// ─── ExportModal.jsx — Reusable CSV export panel ─────────────────────────────
// Renders as a modal overlay. Used by Dashboard and CashCalendar.
// The standalone Export page (Export.jsx) also uses this internally.

import { useState, useMemo } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { BottomSheet } from "./ui.jsx";
import { row, subText } from "../lib/styles.js";
import { FONT, RADIUS } from "../lib/theme.js";
import {
  mkKey, MONTH_NAMES, currSym,
  generateOrderExpenses, generateRecurringItems,
  computeDeliveryExpenses, getMonthlyPayoutBreakdown,
} from "../lib/calculations.js";

const TODAY = new Date();

// ── CSV helper ────────────────────────────────────────────────────────────────
function downloadCSV(filename, rows) {
  const csv = rows.map(r => r.map(cell => {
    const s = String(cell == null ? "" : cell);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? '"' + s.replace(/"/g, '""') + '"' : s;
  }).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const REPORT_TYPES = [
  { id: "summary",  icon: "📊", label: "Monthly Cashflow Summary",  desc: "One row per month — income, expenses, net, balance" },
  { id: "expenses", icon: "💸", label: "Itemized Expenses",          desc: "Every expense line — manual, orders, recurring, partners" },
  { id: "income",   icon: "💰", label: "Itemized Income",            desc: "Income by marketplace and product" },
  { id: "orders",   icon: "📦", label: "Purchase Orders",            desc: "Orders with payment terms, due dates, paid status" },
];

// Tiers that include CSV/PDF export — Pro + Business.
const PAID_EXPORT_TIERS = new Set(["pro", "business"]);

// ── ExportPanel — the actual form (used inside modal and standalone page) ─────
export function ExportPanel({ months, orders, marketplaces, visibleYears, recurringIncome, recurringExpenses, partners, deliveryCreditLimit, fbmSettings, account }) {
  const T = useTheme();
  const [loading, setLoading]               = useState(false);
  const [selectedReports, setSelectedReports] = useState(["summary"]);
  const [rangeType, setRangeType]           = useState("all");
  const [selectedYears, setSelectedYears]   = useState([TODAY.getFullYear()]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const sym = currSym();

  // Pro gate — Starter accounts see an upgrade prompt instead of the form.
  // Exports are available on Pro + Business only.
  const planTier = account?.plan_tier || "starter";
  const canExportTier = PAID_EXPORT_TIERS.has(planTier);
  if (!canExportTier) {
    return (
      <div style={{ padding:"16px 4px", display:"flex", flexDirection:"column", gap:16 }}>
        <div style={{
          padding:"24px 22px", borderRadius:RADIUS.lg,
          background:"linear-gradient(135deg, rgba(139,92,246,0.12), rgba(6,182,212,0.08))",
          border:`1px dashed ${T.accent}55`, textAlign:"center",
          fontFamily:FONT,
        }}>
          <div style={{ fontSize:32, marginBottom:8 }}>🔒</div>
          <div style={{ fontSize:16, fontWeight:800, color:T.text, marginBottom:6, letterSpacing:-0.3 }}>
            Exports are a Pro feature
          </div>
          <div style={{ fontSize:13, color:T.textSub, lineHeight:1.55, maxWidth:360, margin:"0 auto 16px" }}>
            Upgrade to Pro to download CSV + PDF reports of your cashflow,
            expenses, income, and purchase orders — ready for accounting,
            tax season, or investor updates.
          </div>
          <a href="#paywall" onClick={(e) => {
            e.preventDefault();
            try { window.location.hash = "settings"; } catch {}
          }} style={{
            display:"inline-block", padding:"10px 24px", borderRadius:RADIUS.md,
            background:`linear-gradient(135deg, #8b5cf6, #06b6d4)`,
            color:"#fff", fontSize:13, fontWeight:700, textDecoration:"none",
            boxShadow:"0 6px 20px rgba(139,92,246,0.35)", fontFamily:FONT,
          }}>Upgrade to Pro →</a>
        </div>
        <div style={{ fontSize:11, color:T.textMuted, textAlign:"center" }}>
          Your data is safe. Exports unlock the moment you upgrade — no re-setup needed.
        </div>
      </div>
    );
  }

  // White-label (Business tier): override CSV filename + add a brand line at the top
  const brandName = account?.brand_export_name || null;
  const brandLogo = account?.brand_logo_url || null;
  const fileSlug = (brandName || "cashhorizon").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "cashhorizon";
  const today = TODAY.toISOString().slice(0, 10);

  function brandHeaderRows(reportTitle) {
    if (!brandName && !brandLogo) return [];
    const rows = [];
    if (brandName) rows.push([brandName]);
    if (brandLogo) rows.push([`Logo: ${brandLogo}`]);
    rows.push([`${reportTitle} · Generated ${today}`]);
    rows.push([]);
    return rows;
  }

  const allRecurring   = useMemo(() => [...(recurringIncome||[]), ...(recurringExpenses||[])], [recurringIncome, recurringExpenses]);
  const orderExpenses  = useMemo(() => generateOrderExpenses(orders||[]), [orders]);
  const recurringItems = useMemo(() => generateRecurringItems(allRecurring, visibleYears||[TODAY.getFullYear()]), [allRecurring, visibleYears]);
  const deliveryExps   = useMemo(() => computeDeliveryExpenses(months||{}, marketplaces||[], deliveryCreditLimit||0, fbmSettings||[]), [months, marketplaces, deliveryCreditLimit, fbmSettings]);

  const allKeys = useMemo(() => {
    const keys = [];
    for (const y of (visibleYears||[TODAY.getFullYear()]).sort()) {
      for (let m = 1; m <= 12; m++) keys.push(mkKey(y, m));
    }
    return keys;
  }, [visibleYears]);

  const exportKeys = useMemo(() => {
    if (rangeType === "all")    return allKeys;
    if (rangeType === "year")   return allKeys.filter(k => selectedYears.includes(parseInt(k.split("-")[0])));
    if (rangeType === "custom") return selectedMonths.length > 0 ? [...selectedMonths].sort() : allKeys;
    return allKeys;
  }, [rangeType, allKeys, selectedYears, selectedMonths]);

  function toggleReport(id) { setSelectedReports(p => p.includes(id) ? p.filter(r=>r!==id) : [...p,id]); }
  function toggleYear(y)    { setSelectedYears(p => p.includes(y) ? p.filter(x=>x!==y) : [...p,y]); }
  function toggleMonth(key) { setSelectedMonths(p => p.includes(key) ? p.filter(k=>k!==key) : [...p,key]); }

  function runExports() {
    if (!selectedReports.length) return;
    setLoading(true);
    setTimeout(() => {
      try {
        if (selectedReports.includes("summary"))  exportSummary();
        if (selectedReports.includes("expenses")) exportExpenses();
        if (selectedReports.includes("income"))   exportIncome();
        if (selectedReports.includes("orders"))   exportOrders();
      } finally { setLoading(false); }
    }, 50);
  }

  function exportSummary() {
    const rows = [["Month", "Target Units", `Income (${sym})`, `Expenses (${sym})`, `Net (${sym})`, `Balance (${sym})`]];
    let cumulative = 0;
    for (const key of exportKeys) {
      const [yr, mo] = key.split("-").map(Number);
      const mData = months[key] || {};
      const breakdown = getMonthlyPayoutBreakdown(key, marketplaces||[], months||{});
      const income = breakdown.reduce((s,b)=>s+b.payout,0)
        + (recurringItems[key]||[]).filter(i=>i.isIncome).reduce((s,i)=>s+i.amount,0)
        + (mData.incomeItems||[]).reduce((s,e)=>s+e.amount,0);
      const expenses = (mData.expenses||[]).reduce((s,e)=>s+e.amount,0)
        + (deliveryExps[key]||[]).reduce((s,e)=>s+e.amount,0)
        + (orderExpenses[key]||[]).reduce((s,e)=>s+e.amount,0)
        + (recurringItems[key]||[]).filter(i=>!i.isIncome).reduce((s,i)=>s+i.amount,0)
        + (partners||[]).filter(p=>(p.activeMonths||[1,2,3,4,5,6,7,8,9,10,11,12]).includes(mo)).reduce((s,p)=>s+p.amount,0);
      const net = income - expenses;
      cumulative += net;
      rows.push([MONTH_NAMES[mo-1]+" "+yr, mData.target||0, income.toFixed(2), expenses.toFixed(2), net.toFixed(2), cumulative.toFixed(2)]);
    }
    downloadCSV(`${fileSlug}-summary.csv`, [...brandHeaderRows("Cashflow Summary"), ...rows]);
  }

  function exportExpenses() {
    const rows = [["Month","Date","Description","Type",`Amount (${sym})`,"Status"]];
    for (const key of exportKeys) {
      const [yr, mo] = key.split("-").map(Number);
      const label = MONTH_NAMES[mo-1]+" "+yr;
      const mData = months[key] || {};
      for (const e of (mData.expenses||[]))
        rows.push([label, e.date||"—", e.what||"Expense", "Manual", (e.amount||0).toFixed(2), e.paid?"Paid":"Pending"]);
      for (const e of (deliveryExps[key]||[]))
        rows.push([label, "—", e.description||"Fulfilment", "Fulfilment", (e.amount||0).toFixed(2), ((mData.deliveryOverrides||{})[e.id]?.paid)?"Paid":"Pending"]);
      for (const e of (orderExpenses[key]||[]))
        rows.push([label, "—", e.description||"Order payment", "Order", (e.amount||0).toFixed(2), ((mData.orderExpPaid||{})[e.id])?"Paid":"Pending"]);
      for (const i of (recurringItems[key]||[]).filter(i=>!i.isIncome))
        rows.push([label, "—", i.what||i.name||"Recurring", "Recurring", (i.amount||0).toFixed(2), "Pending"]);
      for (const p of (partners||[]))
        if ((p.activeMonths||[1,2,3,4,5,6,7,8,9,10,11,12]).includes(mo))
          rows.push([label, "—", p.name||"Partner", "Partner", (p.amount||0).toFixed(2), "—"]);
    }
    downloadCSV(`${fileSlug}-expenses.csv`, [...brandHeaderRows("Itemized Expenses"), ...rows]);
  }

  function exportIncome() {
    const rows = [["Month","Marketplace","Product","Units",`Payout/unit (${sym})`,`Total (${sym})`]];
    for (const key of exportKeys) {
      const [yr, mo] = key.split("-").map(Number);
      const label = MONTH_NAMES[mo-1]+" "+yr;
      const breakdown = getMonthlyPayoutBreakdown(key, marketplaces||[], months||{});
      for (const mkt of breakdown)
        for (const prod of mkt.products)
          if (prod.units > 0)
            rows.push([label, mkt.mktName, prod.prodName, prod.units, prod.units>0?(prod.payout/prod.units).toFixed(2):"0", prod.payout.toFixed(2)]);
      for (const i of (recurringItems[key]||[]).filter(i=>i.isIncome))
        rows.push([label, "—", i.what||i.name||"Recurring Income", "—", "—", (i.amount||0).toFixed(2)]);
    }
    downloadCSV(`${fileSlug}-income.csv`, [...brandHeaderRows("Itemized Income"), ...rows]);
  }

  function exportOrders() {
    const rows = [["Order","Order Date",`Total (${sym})`,"Containers","Term","%",`Amount (${sym})`,"Due Date","Status",`Shipping/container (${sym})`]];
    for (const order of (orders||[])) {
      const totalCt = (order.containerLines||[]).reduce((s,l)=>s+(l.qty||0),0);
      const shipping = order.shippingCostPerContainer||0;
      if (!(order.paymentTerms||[]).length) {
        rows.push([order.number||"—", order.orderDate||"—", (order.amount||0).toFixed(2), totalCt, "—","—",(order.amount||0).toFixed(2),"—","—",shipping.toFixed(2)]);
      } else {
        for (const pt of order.paymentTerms) {
          const amt = (order.amount||0)*(pt.pct||0)/100;
          rows.push([order.number||"—", order.orderDate||"—", (order.amount||0).toFixed(2), totalCt, pt.label||"Payment",(pt.pct||0)+"%",amt.toFixed(2),pt.date||"—",pt.paid?"Paid":"Pending",shipping.toFixed(2)]);
        }
      }
    }
    downloadCSV(`${fileSlug}-orders.csv`, [...brandHeaderRows("Purchase Orders"), ...rows]);
  }

  const canExport = selectedReports.length > 0 && exportKeys.length > 0;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* Report type */}
      <div>
        <div style={{ fontSize:11, fontWeight:700, color:T.textSub, letterSpacing:0.8, textTransform:"uppercase", marginBottom:10 }}>Choose Reports</div>
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {REPORT_TYPES.map(rt => {
            const sel = selectedReports.includes(rt.id);
            return (
              <button key={rt.id} onClick={() => toggleReport(rt.id)} style={{
                display:"flex", alignItems:"center", gap:12, padding:"10px 14px",
                background: sel ? T.accent+"15" : T.surface,
                border:`1px solid ${sel ? T.accent : T.border}`,
                borderRadius:RADIUS.md, cursor:"pointer", textAlign:"left", fontFamily:FONT,
                transition:"all 0.15s",
              }}>
                <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${sel?T.accent:T.border}`, background:sel?T.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {sel && <span style={{ color:"#fff", fontSize:11, fontWeight:800 }}>✓</span>}
                </div>
                <span style={{ fontSize:16 }}>{rt.icon}</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:sel?700:500, color:sel?T.accent:T.text }}>{rt.label}</div>
                  <div style={{ fontSize:11, color:T.textSub, marginTop:1 }}>{rt.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Date range */}
      <div>
        <div style={{ fontSize:11, fontWeight:700, color:T.textSub, letterSpacing:0.8, textTransform:"uppercase", marginBottom:10 }}>Select Period</div>
        <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
          {[["all","All time"],["year","By year"],["custom","Pick months"]].map(([v,l]) => (
            <button key={v} onClick={() => setRangeType(v)} style={{
              padding:"6px 14px", borderRadius:RADIUS.pill,
              background: rangeType===v ? T.accent+"18" : "transparent",
              color: rangeType===v ? T.accent : T.textSub,
              border:`1px solid ${rangeType===v ? T.accent : T.border}`,
              fontSize:12, fontFamily:FONT, cursor:"pointer", fontWeight:rangeType===v?700:400,
            }}>{l}</button>
          ))}
        </div>

        {rangeType === "year" && (
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {(visibleYears||[TODAY.getFullYear()]).sort().map(y => {
              const sel = selectedYears.includes(y);
              return (
                <button key={y} onClick={() => toggleYear(y)} style={{
                  padding:"6px 16px", borderRadius:RADIUS.sm,
                  background: sel ? T.accent+"22" : "transparent",
                  color: sel ? T.accent : T.textSub,
                  border:`1px solid ${sel ? T.accent : T.border}`,
                  fontSize:13, fontFamily:FONT, cursor:"pointer", fontWeight:sel?700:400,
                }}>{y}</button>
              );
            })}
          </div>
        )}

        {rangeType === "custom" && (
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {(visibleYears||[TODAY.getFullYear()]).sort().map(y => (
              <div key={y}>
                <div style={{ fontSize:11, fontWeight:700, color:T.textSub, marginBottom:5, display:"flex", alignItems:"center", gap:8 }}>
                  {y}
                  <button onClick={() => {
                    const yKeys = Array.from({length:12},(_,i)=>mkKey(y,i+1));
                    const allSel = yKeys.every(k=>selectedMonths.includes(k));
                    setSelectedMonths(prev => allSel ? prev.filter(k=>!yKeys.includes(k)) : [...new Set([...prev,...yKeys])]);
                  }} style={{ background:"none", border:"none", color:T.accent, fontSize:10, cursor:"pointer", fontFamily:FONT }}>
                    {Array.from({length:12},(_,i)=>mkKey(y,i+1)).every(k=>selectedMonths.includes(k)) ? "Deselect all" : "Select all"}
                  </button>
                </div>
                <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                  {Array.from({length:12},(_,i) => {
                    const key = mkKey(y,i+1);
                    const sel = selectedMonths.includes(key);
                    return (
                      <button key={key} onClick={() => toggleMonth(key)} style={{
                        padding:"4px 10px", borderRadius:RADIUS.sm,
                        background: sel ? T.accent+"22" : "transparent",
                        color: sel ? T.accent : T.textSub,
                        border:`1px solid ${sel ? T.accent : T.border}`,
                        fontSize:11, fontFamily:FONT, cursor:"pointer", fontWeight:sel?700:400,
                      }}>{MONTH_NAMES[i].slice(0,3)}</button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop:10, fontSize:11, color:T.textSub }}>
          {exportKeys.length} month{exportKeys.length!==1?"s":""} selected
          {exportKeys.length > 0 && ` · ${exportKeys[0].split("-")[0]}${exportKeys[0].split("-")[0] !== exportKeys[exportKeys.length-1].split("-")[0] ? " – "+exportKeys[exportKeys.length-1].split("-")[0] : ""}`}
        </div>
      </div>

      {/* Export button */}
      <button onClick={runExports} disabled={!canExport || loading} style={{
        width:"100%", padding:"13px",
        borderRadius:RADIUS.lg,
        background: canExport ? T.accent : T.surfaceHi,
        color: canExport ? "#fff" : T.textMuted,
        border:"none", fontSize:15, fontWeight:700, fontFamily:FONT,
        cursor: canExport ? "pointer" : "not-allowed",
        transition:"all 0.2s",
      }}>
        {loading ? "⟳ Generating…" : `⬇ Export ${selectedReports.length} Report${selectedReports.length!==1?"s":""} (${exportKeys.length} months)`}
      </button>

      <div style={{ fontSize:11, color:T.textMuted, textAlign:"center" }}>
        Files generated locally — your data never leaves your browser.
      </div>
    </div>
  );
}

// ── ExportModal — overlay wrapper ─────────────────────────────────────────────
export default function ExportModal({ open, onClose, ...props }) {
  return (
    <BottomSheet open={open} onClose={onClose} title="📤 Export Data"
      subtitle="CSV files — open directly in Excel or Google Sheets" height="75vh">
      <ExportPanel {...props}/>
    </BottomSheet>
  );
}
